import { useEffect, MutableRefObject, useRef } from 'react';
import { abrirArquivoComCredenciais } from '../../../lib/corel/actions/abrirArquivo';
import type { SmbCredentials } from '../../../lib/corel/actions/abrirArquivo';
import { corelCommunication } from '../../../lib/corel/communication';
import { prepareSmbCredentials } from '../../../utils/smbCredentialsHelper';
import { writeSnapshot, setPreparedPipelineId, clearPreparedPipeline } from '../../../lib/corel/pipeline/pipelineKeys';
import { OPERATION_DELAYS, pipelineSleep } from '../../../lib/corel/pipeline/pipelineConstants';
import { requestConfirmation } from '../../../lib/globalConfirm';
import { notify } from '../../../lib/globalNotify';
import { getFileOpenTimeoutMessage } from '../../../lib/corel/timeoutMessages';
import { logPipelineStage, logPipelineError } from '../../../lib/corel/logging/pipelineLogger';
import { monitoradorObjetos } from '../../../lib/corel/MonitoradorObjetos';

type OrderLike = { orc_id?: number | string | null } | null | undefined;
type ItemLike = { oit_id?: number | string | null } | null | undefined;

interface StorageLike {
  get: <T = unknown>(key: string, options?: any) => T | null;
  set: <T = unknown>(key: string, value: T, options?: any) => void;
}

interface UseProductionOpenFlowParams {
  role: string | null | undefined;
  selectedPedido: OrderLike;
  selectedItem: ItemLike;
  resolveCaminhoArquivo: (item: ItemLike) => string | null;
  resetAllModals: () => void;
  notificarWebViewDisponivel: () => Promise<void>;
  setShowProdPrep: (value: boolean) => void;
  setProdPrepStep: (step: 'open-file' | 'status' | 'init' | 'sync' | 'done') => void;
  setSnapshotApplying: (value: boolean) => void;
  isOpeningFile: boolean;
  setIsOpeningFile: (value: boolean) => void;
  openStartedRef: MutableRefObject<boolean>;
  openCompletedRef: MutableRefObject<boolean>;
  openRequestIdRef: MutableRefObject<string | null>;
  openTimeoutRef: MutableRefObject<ReturnType<typeof setTimeout> | null>;
  storage: StorageLike;
  onClose: () => void;
}

/**
 * Configuração do monitor baseada na documentação oficial.
 * Referência: monitorar-objetos_json.md (linhas 451-454)
 */
interface MonitorConfig {
  pollingInterval: number;
  batchSize: number;
  snapshotPaginado: boolean;
  delayAntesStatus: number;
}

/**
 * Calcula configuração do monitor baseada na quantidade de objetos.
 * Segue as recomendações da documentação oficial:
 * - < 100 objetos: pollingInterval: 200ms, batchSize: 1
 * - 100-500 objetos: pollingInterval: 300ms, batchSize: 5
 * - 500+ objetos: pollingInterval: 400ms, batchSize: 15
 * 
 * @param numeroObjetos Quantidade de objetos no documento
 * @returns Configuração completa do monitor
 */
function calcularConfiguracaoMonitor(numeroObjetos: number): MonitorConfig {
  if (numeroObjetos >= 500) {
    return {
      pollingInterval: 400,
      batchSize: 15,
      snapshotPaginado: true,
      delayAntesStatus: 600 // 1.5 × 400ms
    };
  } else if (numeroObjetos >= 100) {
    return {
      pollingInterval: 300,
      batchSize: 5,
      snapshotPaginado: true,
      delayAntesStatus: 450 // 1.5 × 300ms
    };
  } else {
    // < 100 objetos
    return {
      pollingInterval: 200,
      batchSize: 1,
      snapshotPaginado: false,
      delayAntesStatus: 300 // 1.5 × 200ms
    };
  }
}

/**
 * Valida convergência entre numeroObjetos e objetosMonitorados.
 * @param numeroObjetos Contagem do abrir-arquivo
 * @param objetosMonitorados Contagem do monitor status
 * @returns Objeto com validação e valor a usar
 */
function validarConvergencia(numeroObjetos: number, objetosMonitorados: number): {
  convergente: boolean;
  divergenciaPercentual: number;
  valorAUsar: number;
  warning?: string;
} {
  if (numeroObjetos === 0 || objetosMonitorados === 0) {
    return {
      convergente: true,
      divergenciaPercentual: 0,
      valorAUsar: Math.max(numeroObjetos, objetosMonitorados)
    };
  }

  const divergencia = Math.abs(numeroObjetos - objetosMonitorados);
  const divergenciaPercentual = (divergencia / Math.max(numeroObjetos, objetosMonitorados)) * 100;

  // Tolerância de 10% de divergência
  const convergente = divergenciaPercentual <= 10;

  // Usar menor valor (mais conservador)
  const valorAUsar = Math.min(numeroObjetos, objetosMonitorados);

  let warning: string | undefined;
  if (!convergente) {
    warning = `Contagem divergente: esperado ${numeroObjetos}, monitor ${objetosMonitorados} (${divergenciaPercentual.toFixed(1)}% de divergência)`;
  }

  return {
    convergente,
    divergenciaPercentual,
    valorAUsar,
    warning
  };
}

/**
 * Helper para limpar storage de produção.
 * Centraliza a lógica de limpeza para evitar duplicação.
 * 
 * ⚠️ NOTA: Esta função já é chamada automaticamente por cleanupOnCriticalError.
 * Não é necessário chamá-la manualmente no onClose do ProductionOrdersPage.
 * 
 * @param pid ID do pedido
 * @param iid ID do item
 */
export function clearProductionStorage(pid: number | string, iid: number | string): void {
  try {
    clearPreparedPipeline(pid, iid);
    sessionStorage.removeItem(`corel:open:v1:${pid}:${iid}`);
    sessionStorage.removeItem(`prod-open-executed:${pid}:${iid}`);
  } catch (err) {
    // Silencioso - cleanup best effort
  }
}

export function useProductionOpenFlow(params: UseProductionOpenFlowParams) {
  const {
    role,
    selectedPedido,
    selectedItem,
    resolveCaminhoArquivo,
    resetAllModals,
    notificarWebViewDisponivel,
    setShowProdPrep,
    setProdPrepStep,
    setSnapshotApplying,
    isOpeningFile,
    setIsOpeningFile,
    openStartedRef,
    openCompletedRef,
    openRequestIdRef,
    openTimeoutRef,
    storage,
    onClose,
  } = params;

  // ✅ FASE 1.1: Correção Bug Crítico - executionLockRef
  // Ref para prevenir execuções concorrentes da pipeline
  const executionLockRef = useRef<boolean>(false);

  // ============================================================================
  // LIFECYCLE DO MONITOR
  // ============================================================================
  
  /**
   * ⚠️ IMPORTANTE: Este hook NÃO gerencia o lifecycle do monitor.
   * 
   * O monitor é iniciado na pipeline e parado quando OrderDetailsWindow desmonta.
   * Este hook apenas utiliza o monitor que já está ativo.
   */

  // ✅ FASE 4: Função de cleanup em erros críticos
  /**
   * Cleanup completo após erro crítico.
   * Garante que todos os recursos são liberados e o estado é resetado.
   * Retorna o usuário ao menu principal após cleanup.
   * 
   * @param pipelineId ID único da pipeline
   * @param reason Razão do erro crítico
   * @param partialMetrics Métricas parciais capturadas até o momento do erro (opcional)
   */
  const cleanupOnCriticalError = async (
    pipelineId: string, 
    reason: string,
    partialMetrics?: {
      openMs?: number;
      monitorInitMs?: number;
      monitorStatusMs?: number;
      syncMs?: number;
    }
  ) => {
    logPipelineError(pipelineId, 'CRITICAL_ERROR_CLEANUP', { 
      message: `Cleanup triggered - Reason: ${reason}`,
      reason,
      ...(partialMetrics && { partialMetrics })
    });
    
    // 1. Limpar refs
    executionLockRef.current = false;
    openStartedRef.current = false;
    openCompletedRef.current = false;
    
    // 2. Limpar timeouts
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }
    
    // 3. Parar monitor se ativo (COM RETRY - crítico para evitar monitor órfão)
    const cleanupStopResult = await monitoradorObjetos.stopWithRetry(2, 2000);
    
    if (!cleanupStopResult.success) {
      logPipelineError(pipelineId, 'CLEANUP_MONITOR_FAILED', {
        warning: 'Monitor pode estar ativo no background - próxima execução deve verificar',
        error: cleanupStopResult.error
      });
      
      // Setar flag para próxima execução verificar e forçar limpeza
      try {
        sessionStorage.setItem('monitor-cleanup-failed', Date.now().toString());
      } catch {
        // Silencioso
      }
    } else {
      await pipelineSleep(OPERATION_DELAYS.AFTER_MONITOR_STOP, 'cleanup_monitor');
    }
    
    // 4. Limpar storage
    const pid = selectedPedido?.orc_id;
    const iid = selectedItem?.oit_id;
    if (pid && iid) {
      clearProductionStorage(pid, iid);
    }
    
    // 5. Resetar UI
    setIsOpeningFile(false);
    setProdPrepStep('done');
    setSnapshotApplying(false);
    setShowProdPrep(false);
    
    // 6. Fechar e voltar ao menu (com pequeno delay para UI se atualizar)
    setTimeout(() => {
      onClose();
    }, 100);
  };

  useEffect(() => {
    if (role !== 'producao') return;
    
    const pid = selectedPedido?.orc_id;
    const iid = selectedItem?.oit_id;
    
    // ✅ GUARD 0: Se já está marcado como completo, não fazer nada
    if (openCompletedRef.current) {
      return;
    }
    
    // ✅ GUARD 0.5: Se snapshot já existe (pipeline de produção já executou), não fazer nada
    // A nova pipeline runProductionOpenPipeline já preparou tudo antes de montar OrderDetailsWindow
    if (pid && iid) {
      try {
        // Usar mesma chave que pipelineKeys.ts: corel:pipeline:prepared:v1:${orcId}:${oitId}
        const existingPipelineId = sessionStorage.getItem(`corel:pipeline:prepared:v1:${pid}:${iid}`);
        if (existingPipelineId) {
          sendToTerminal('⏭️ [PROD_OPEN_FLOW] SKIP - Pipeline já executou (snapshot existe)', {
            pid,
            iid,
            existingPipelineId
          });
          // ✅ Marcar como completo para evitar re-execução
          openCompletedRef.current = true;
          return;
        }
      } catch {}
    }
    
    // ✅ GUARD 1: Evitar execução se já está executando
    if (isOpeningFile || openStartedRef.current || executionLockRef.current) {
      return;
    }
    
    // ✅ GUARD 2: Evitar múltiplas execuções para o mesmo pedido/item em curto período
    if (!pid || !iid) {
      return;
    }
    
    // ✅ GUARD 3: Validar caminho antes de processar
    const caminho = resolveCaminhoArquivo(selectedItem);
    if (!caminho) {
      return;
    }
    
    const executionKey = `prod-open-executed:${pid}:${iid}`;
    try {
      const lastExecution = sessionStorage.getItem(executionKey);
      if (lastExecution) {
        const timeSince = Date.now() - Number(lastExecution);
        if (timeSince < 5000) {
          return;
        }
      }
    } catch {}

    const openKey = `corel:open:v1:${selectedPedido?.orc_id}:${selectedItem?.oit_id}`;
    const now = Date.now();
    
    let recentOpen = false;
    try {
      const lastStored = storage.get<string>(openKey, { area: 'session' });
      const last = Number(lastStored ?? '0');
      recentOpen = last > 0 && (now - last) < 4000;
    } catch (error) {
      // Silencioso - erro não crítico
    }

    // ✅ GUARD FINAL: Verificar todas as condições ANTES de iniciar pipeline
    if (!caminho || isOpeningFile || openStartedRef.current || openCompletedRef.current || recentOpen) {
      return;
    }

    // ✅ FASE 2.2: Pipeline ID único - SÓ LOGAR SE REALMENTE VAI EXECUTAR
    const pipelineId = `prod-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    
    logPipelineStage(pipelineId, 'START', { pid, iid });

    resetAllModals();

    // ✅ A partir daqui, pipeline vai realmente executar
    if (true) {  // Sempre executa (guards já verificados acima)
      // ✅ Registrar início da execução
      try {
        sessionStorage.setItem(executionKey, String(Date.now()));
      } catch (error) {
        // Silencioso
      }
      
      openStartedRef.current = true;
      executionLockRef.current = true;
      setIsOpeningFile(true);
      try { 
        storage.set<string>(openKey, String(now), { area: 'session' }); 
      } catch (error) {
        // Silencioso
      }

      try {
        setShowProdPrep(true);
        setProdPrepStep('open-file');
      } catch {}

      // ✅ Preparar credenciais SMB
      (async () => {
        // ✅ FASE 6: Métricas de performance
        const metrics = {
          startedAt: Date.now(),
          openMs: 0,
          monitorInitMs: 0,
          monitorStatusMs: 0,
          syncMs: 0,
          totalMs: 0
        };
        
        let smbCredentials: SmbCredentials | undefined = undefined;
        
        try {
          const regras = (selectedItem as any)?.regras;
          
          if (regras) {
            const smbResult = await prepareSmbCredentials({
              source: 'rules',
              rules: regras,
              path: caminho,
              context: 'PROD-OPEN',
              forceSmb: false
            });
            
            // ✅ CORRIGIDO: Verificar success PRIMEIRO
            if (!smbResult.success) {
              // ❌ Erro de API - BLOQUEAR operação
              await cleanupOnCriticalError(pipelineId, 'SMB_API_ERROR');
              notify({
                type: 'error',
                title: 'Erro ao buscar credenciais SMB',
                message:
                  'Não foi possível buscar as credenciais de rede.\n\n' +
                  `Erro: ${smbResult.error || 'Erro desconhecido'}\n\n` +
                  'Verifique sua conexão e tente novamente.',
                duration: 12000,
              });
              return;
            }
            
            // ✅ API OK - verificar se tem credenciais
            if (smbResult.hasCredentials && smbResult.credentials) {
              smbCredentials = {
                username: smbResult.credentials.username,
                password: smbResult.credentials.password,
                ...(smbResult.credentials.domain ? { domain: smbResult.credentials.domain } : {})
              };
            } else {
              // ✅ API OK mas SEM credenciais - verificar se é obrigatório
              const isNetworkPath = caminho.startsWith('\\\\') || /^[A-Z]:\\/i.test(caminho);
              const requiresSmb = regras?.requer_smb === true || regras?.smb_obrigatorio === true;
              
              if (isNetworkPath && requiresSmb && !smbCredentials) {
                // ❌ Falha crítica: arquivo de rede requer SMB mas não tem credenciais
                await cleanupOnCriticalError(pipelineId, 'SMB_REQUIRED_BUT_MISSING');
                notify({
                  type: 'error',
                  title: 'Credenciais de rede necessárias',
                  message:
                    'Este arquivo está em uma rede e requer credenciais SMB, mas não foram encontradas nas regras.\n\n' +
                    'Verifique:\n' +
                    '• Regras do item estão configuradas corretamente\n' +
                    '• Credenciais SMB estão cadastradas no sistema\n\n' +
                    `Caminho: ${caminho.substring(0, 80)}${caminho.length > 80 ? '...' : ''}`,
                  duration: 12000,
                });
                return;
              }
              
              // Log warning se é caminho de rede mas SMB não é obrigatório
              if (isNetworkPath && !smbCredentials) {
                logPipelineError(pipelineId, 'SMB_CREDENTIALS_WARNING', {
                  message: 'Arquivo de rede sem credenciais SMB - tentará abrir sem autenticação',
                  isNetworkPath,
                  requiresSmb
                });
              }
            }
          }
        } catch (error) {
          // ❌ Exception não tratada - BLOQUEAR
          logPipelineError(pipelineId, 'SMB_CREDENTIALS_EXCEPTION', error instanceof Error ? error.message : String(error));
          
          const regras = (selectedItem as any)?.regras;
          const isNetworkPath = caminho.startsWith('\\\\') || /^[A-Z]:\\/i.test(caminho);
          const requiresSmb = regras?.requer_smb === true || regras?.smb_obrigatorio === true;
          
          if (isNetworkPath && requiresSmb) {
            await cleanupOnCriticalError(pipelineId, 'SMB_CREDENTIALS_ERROR');
            notify({
              type: 'error',
              title: 'Erro ao obter credenciais de rede',
              message:
                'Ocorreu um erro ao preparar as credenciais SMB necessárias.\n\n' +
                `Erro: ${error instanceof Error ? error.message : String(error)}\n\n` +
                'Tente novamente ou contate o suporte.',
              duration: 12000,
            });
            return;
          }
        }

        // 🔍 VERIFICAR ESTADO DO MONITOR ANTES DE ABRIR
        try {
          let monitorAtivo = false;
          const PRE_CHECK_TIMEOUT = 3000; // 3s mais seguro que 1s
          
          const preCheckStatus = await monitoradorObjetos.verificarStatus();
          monitorAtivo = preCheckStatus?.ativo === true;
          
          logPipelineStage(pipelineId, 'MONITOR_PRE_CHECK', { 
            monitorAtivo,
            docKey: preCheckStatus?.docKey
          });
          
          // Se monitor está ativo, parar antes de abrir (COM RETRY)
          if (monitorAtivo) {
            logPipelineStage(pipelineId, 'MONITOR_STOP_BEFORE_OPEN', { monitorAtivo });
            
            const stopResult = await monitoradorObjetos.stopWithRetry(3, 2000);
            
            if (!stopResult.success) {
              // ❌ Falha crítica - não podemos continuar com monitor ativo
              logPipelineError(pipelineId, 'MONITOR_STOP_FAILED', stopResult);
              await cleanupOnCriticalError(pipelineId, 'MONITOR_STOP_FAILED');
              notify({
                type: 'error',
                title: 'Não foi possível parar o monitor anterior',
                message:
                  'O sistema tentou 3 vezes mas não conseguiu.\n\n' +
                  'Possíveis causas:\n' +
                  '• CorelDRAW está travado\n' +
                  '• Plugin C# não está respondendo\n\n' +
                  'Reinicie o CorelDRAW e tente novamente.',
                duration: 12000,
              });
              return;
            }
            
            // Aguardar limpeza do estado do monitor
            await pipelineSleep(OPERATION_DELAYS.AFTER_MONITOR_STOP, 'after_monitor_stop');
          }
        } catch (err) {
          logPipelineError(pipelineId, 'MONITOR_CHECK', String(err));
        }
        const requestId = abrirArquivoComCredenciais(caminho, smbCredentials, (response) => {
          const openElapsed = Date.now() - metrics.startedAt;
          
          if (openRequestIdRef.current && requestId !== openRequestIdRef.current) {
            return;
          }
          
          // ✅ Capturar informações da resposta
          const result = (response as any)?.result || {};
          const numeroObjetos = result?.numeroObjetos ?? 0;
          const hashArquivo = result?.hashArquivo ?? null;
          const tempoAbertura = result?.tempoAbertura ?? 0;
          const coberturaMetadados = result?.validacaoMetadados?.percentualCobertura ?? 
                                     result?.coberturaMetadados ?? 0; // Fallback para estrutura antiga

          if (response.status === 'ok' && response.result?.sucesso === true) {
            metrics.openMs = openElapsed;
            
            logPipelineStage(pipelineId, 'OPEN_FILE_OK', { 
              numeroObjetos, 
              hashArquivo, 
              tempoAbertura, 
              coberturaMetadados,
              openMs: metrics.openMs
            });
            
            setIsOpeningFile(false);
            openCompletedRef.current = true;
            openStartedRef.current = false;
            
            if (openTimeoutRef.current) {
              clearTimeout(openTimeoutRef.current);
              openTimeoutRef.current = null;
            }

            // ✅ VALIDAÇÃO CRÍTICA #1: Verificar se arquivo tem objetos
            if (numeroObjetos === 0) {
              (async () => {
                await cleanupOnCriticalError(pipelineId, 'FILE_EMPTY', { openMs: metrics.openMs });
                notify({
                  type: 'error',
                  title: 'Arquivo sem objetos válidos',
                  message: 'O arquivo foi aberto, mas nenhum objeto foi encontrado. Verifique se o arquivo está correto.',
                  duration: 12000,
                });
              })();
              return;
            }

            // ✅ CALCULAR CONFIGURAÇÃO DO MONITOR IMEDIATAMENTE
            const monitorConfig = calcularConfiguracaoMonitor(numeroObjetos);

            (async () => {
              // ✅ REMOVIDO: setSnapshotApplying(true) - redundante, showProdPrep já controla o loading durante a pipeline
              // snapshotApplying só é necessário quando useSnapshotHydration aplica o snapshot após a pipeline
              
              // ✅ Delay mínimo para estabilização
              await pipelineSleep(OPERATION_DELAYS.MIN_STABILIZATION, 'min_stabilization');
              
              try { setProdPrepStep('status'); } catch {}
              
              try { 
                await notificarWebViewDisponivel(); 
              } catch {}
              
              // ⭐ Pipeline de monitoramento
              let monitorInitOk = false;
              let monitorStatusOk = false;
              let pinnedDocKey: string | null = null;
              let syncedObjetos: any[] = []; // ✅ CORREÇÃO: Declarar aqui para estar acessível no FINISH
              let expectedObjects = numeroObjetos; // ✅ CORREÇÃO: Declarar aqui para estar acessível em todo escopo
              
              try {
                setProdPrepStep('init');
                
                // ✅ REFACTOR: Usar método dedicado initForProduction() que gerencia
                // todo o ciclo: init → status → sync → validação atomicamente
                // Isso resolve race conditions e garante convergência de objetos
                const initStartTime = Date.now();
                
                let monitorResult = await monitoradorObjetos.initForProduction(caminho);
                
                metrics.monitorInitMs = Date.now() - initStartTime;
                
                // 🐛 DEBUG: Log completo do resultado do initForProduction
                sendToTerminal('🔍 [PROD_OPEN_FLOW] initForProduction retornou', {
                  pipelineId,
                  success: monitorResult.success,
                  hasData: !!monitorResult.data,
                  dataType: typeof monitorResult.data,
                  dataKeys: monitorResult.data ? Object.keys(monitorResult.data) : [],
                  dataPreview: monitorResult.data ? JSON.stringify(monitorResult.data).substring(0, 300) : 'null',
                  error: monitorResult.error,
                  message: monitorResult.message,
                  monitorInitMs: metrics.monitorInitMs
                });
                
                // ✅ Se falhou, perguntar ao usuário se quer tentar novamente
                if (!monitorResult.success) {
                  const errorMsg = monitorResult.error || 'Erro desconhecido ao iniciar monitor';
                  
                  // Log detalhado do erro
                  sendToTerminal('🔍 [PROD_OPEN_FLOW] Detalhes do erro', {
                    pipelineId,
                    error: errorMsg,
                    syncData: monitorResult.data,
                    openMs: metrics.openMs,
                    monitorInitMs: metrics.monitorInitMs
                  });
                  
                  // ✅ Calcular tempo total de delays
                  const totalDelayMs = OPERATION_DELAYS.PRODUCTION_MONITOR_INIT + 
                                      OPERATION_DELAYS.PRODUCTION_AFTER_STATUS + 
                                      OPERATION_DELAYS.PRODUCTION_BEFORE_SYNC;
                  
                  // Perguntar ao usuário se quer tentar novamente
                  const retry = await requestConfirmation({
                    title: 'Falha ao iniciar monitoramento',
                    message:
                      `${errorMsg}\n\n` +
                      `Detalhes técnicos:\n` +
                      `• Tempo de abertura: ${metrics.openMs}ms\n` +
                      `• Tempo de init monitor: ${metrics.monitorInitMs}ms\n` +
                      `• Status do sync: ${monitorResult.data?.sucesso ? 'OK' : 'Falhou'}\n` +
                      `• Objetos encontrados: ${monitorResult.data?.objetos?.length || 0}\n\n` +
                      `Possíveis causas:\n` +
                      `• CorelDRAW ainda está processando o documento\n` +
                      `• Documento pode não ter objetos válidos\n` +
                      `• Plugin C# pode precisar de mais tempo\n\n` +
                      `⏱️ O sistema aguardou ${(totalDelayMs / 1000).toFixed(1)}s (1s + 0.5s + 0.5s) antes de validar.\n\n` +
                      `Deseja tentar novamente?`,
                    confirmLabel: 'Tentar novamente',
                    cancelLabel: 'Cancelar',
                  });
                  
                  if (retry) {
                    sendToTerminal('🔄 [PROD_OPEN_FLOW] Usuário solicitou retry', { pipelineId });
                    
                    // Tentar novamente (sem delay adicional de estabilização)
                    const retryStartTime = Date.now();
                    const retryResult = await monitoradorObjetos.initForProduction(
                      undefined, // Não precisa repassar caminho (já estabilizado)
                      1 // Uma tentativa
                    );
                    
                    const retryMs = Date.now() - retryStartTime;
                    
                    if (!retryResult.success) {
                      // Falhou novamente
                      sendToTerminal('❌ [PROD_OPEN_FLOW] Retry falhou', {
                        pipelineId,
                        error: retryResult.error,
                        retryMs
                      });
                      
                      await cleanupOnCriticalError(pipelineId, 'MONITOR_INIT_FAILED_AFTER_RETRY', {
                        openMs: metrics.openMs,
                        monitorInitMs: metrics.monitorInitMs,
                        retryMs
                      });
                      
                      notify({
                        type: 'error',
                        title: 'Falha persistente no monitoramento',
                        message:
                          `O monitor não conseguiu inicializar mesmo após nova tentativa.\n\n` +
                          `Erro: ${retryResult.error}\n\n` +
                          `Ações recomendadas:\n` +
                          `1. Verifique se o documento tem objetos válidos\n` +
                          `2. Feche e reabra o CorelDRAW\n` +
                          `3. Tente abrir o arquivo novamente`,
                        duration: 12000,
                      });
                      return;
                    }
                    
                    // ✅ Retry bem-sucedido! Atualizar monitorResult e continuar o fluxo
                    sendToTerminal('✅ [PROD_OPEN_FLOW] Retry bem-sucedido!', { 
                      pipelineId,
                      retryMs,
                      objetos: retryResult.data?.objetos?.length || 0
                    });
                    
                    // Atualizar métricas
                    metrics.monitorInitMs = retryMs;
                    
                    // Atualizar variáveis com dados do retry
                    monitorResult = retryResult;
                    
                  } else {
                    // Usuário cancelou
                    sendToTerminal('🚫 [PROD_OPEN_FLOW] Usuário cancelou retry', { pipelineId });
                    
                    await cleanupOnCriticalError(pipelineId, 'MONITOR_INIT_CANCELLED_BY_USER', {
                      openMs: metrics.openMs,
                      monitorInitMs: metrics.monitorInitMs
                    });
                    return;
                  }
                }
                
                // ✅ Extrair dados do resultado (já validados e sincronizados pelo initForProduction)
                syncedObjetos = monitorResult.data?.objetos ?? [];
                const objetosMonitoradosFromInit = monitorResult.data?.objetosMonitorados ?? 0;
                expectedObjects = objetosMonitoradosFromInit;
                
                // Extrair docKey se disponível
                pinnedDocKey = monitorResult.data?.documentoAtivo || monitorResult.data?.docKey || null;
                
                // 🐛 DEBUG: Log dos dados extraídos
                sendToTerminal('🔍 [PROD_OPEN_FLOW] Dados extraídos do initForProduction', {
                  pipelineId,
                  syncedObjetosLength: syncedObjetos.length,
                  syncedObjetosIsArray: Array.isArray(syncedObjetos),
                  objetosMonitoradosFromInit,
                  expectedObjects,
                  hasPinnedDocKey: !!pinnedDocKey,
                  pinnedDocKey: pinnedDocKey?.substring(0, 50),
                  monitorResultDataObjetos: monitorResult.data?.objetos ? 
                    (Array.isArray(monitorResult.data.objetos) ? monitorResult.data.objetos.length : 'NOT_ARRAY') : 
                    'MISSING',
                  monitorResultDataObjetosMonitorados: monitorResult.data?.objetosMonitorados,
                  monitorResultDataKeys: monitorResult.data ? Object.keys(monitorResult.data) : []
                });
                
                // Gate crítico: validar que temos objetos
                if (syncedObjetos.length === 0 || objetosMonitoradosFromInit === 0) {
                  sendToTerminal('❌ [PROD_OPEN_FLOW] VALIDAÇÃO FALHOU - Sem objetos', {
                    pipelineId,
                    syncedObjetosLength: syncedObjetos.length,
                    objetosMonitoradosFromInit,
                    reason: syncedObjetos.length === 0 ? 'syncedObjetos vazio' : 'objetosMonitoradosFromInit zero'
                  });
                  await cleanupOnCriticalError(pipelineId, 'NO_OBJECTS_AFTER_INIT', {
                    openMs: metrics.openMs,
                    monitorInitMs: metrics.monitorInitMs
                  });
                  
                  notify({
                    type: 'error',
                    title: 'Nenhum objeto encontrado',
                    message:
                      'O monitor foi iniciado, mas não encontrou objetos no documento.\n\n' +
                      'Verifique se:\n' +
                      '• O arquivo contém objetos válidos\n' +
                      '• O documento foi aberto corretamente\n' +
                      '• Os objetos não estão ocultos ou bloqueados',
                    duration: 12000,
                  });
                  return;
                }
                
                // Marcar como sucesso (init + status + sync já feitos pelo initForProduction)
                monitorInitOk = true;
                monitorStatusOk = true;
                
                logPipelineStage(pipelineId, 'MONITOR_INIT_AND_SYNC_OK', {
                  objetosCount: syncedObjetos.length,
                  objetosMonitorados: objetosMonitoradosFromInit,
                  docKey: pinnedDocKey,
                  initMs: metrics.monitorInitMs
                });
                
                sendToTerminal('✅ [PROD_OPEN_FLOW] MONITOR_INIT_AND_SYNC_OK - Continuando para persistir snapshot', {
                  pipelineId,
                  objetosCount: syncedObjetos.length,
                  objetosMonitorados: objetosMonitoradosFromInit,
                  docKey: pinnedDocKey,
                  nextStep: 'writeSnapshot'
                });
                
                // ✅ Continuar para persistir snapshot (não marcar 'done' ainda)
                // setProdPrepStep('done') será chamado APÓS writeSnapshot()
                
                // 4. Persistir snapshot
                sendToTerminal('🔍 [PROD_OPEN_FLOW] Iniciando persistência de snapshot', {
                  pipelineId,
                  pid,
                  iid,
                  syncedObjetosLength: syncedObjetos.length,
                  syncedObjetosIsArray: Array.isArray(syncedObjetos),
                  hasPinnedDocKey: !!pinnedDocKey
                });
                
                // Validação: verificar pid e iid antes de operações
                if (!pid || !iid) {
                  sendToTerminal('❌ [PROD_OPEN_FLOW] VALIDAÇÃO FALHOU - pid ou iid inválidos', {
                    pipelineId,
                    pid,
                    iid,
                    pidType: typeof pid,
                    iidType: typeof iid
                  });
                  await cleanupOnCriticalError(pipelineId, 'INVALID_ORDER_DATA');
                  notify({
                    type: 'error',
                    title: 'Erro crítico',
                    message: 'Dados do pedido/item inválidos. Por favor, tente novamente.',
                    duration: 10000,
                  });
                  return;
                }
                
                // Validação: verificar que objetos é array válido
                if (!Array.isArray(syncedObjetos)) {
                  sendToTerminal('❌ [PROD_OPEN_FLOW] VALIDAÇÃO FALHOU - syncedObjetos não é array', {
                    pipelineId,
                    syncedObjetosType: typeof syncedObjetos,
                    syncedObjetosValue: String(syncedObjetos).substring(0, 100)
                  });
                  await cleanupOnCriticalError(pipelineId, 'INVALID_OBJECTS_DATA');
                  notify({
                    type: 'error',
                    title: 'Erro crítico',
                    message: 'Dados de objetos inválidos. Por favor, tente novamente.',
                    duration: 10000,
                  });
                  return;
                }
                
                try {
                  const payload = { 
                    meta: { 
                      docKey: pinnedDocKey,
                      capturedAt: Date.now(), 
                      total: syncedObjetos.length 
                    }, 
                    objetos: syncedObjetos 
                  };
                  
                  sendToTerminal('🔍 [PROD_OPEN_FLOW] Payload criado', {
                    pipelineId,
                    payloadMeta: payload.meta,
                    objetosCount: payload.objetos.length,
                    payloadSizeEstimate: JSON.stringify(payload).length
                  });
                  
                  // Estimar tamanho antes de salvar (proteção contra QuotaExceededError)
                  const payloadString = JSON.stringify(payload);
                  const estimatedSizeBytes = payloadString.length * 2; // UTF-16 = 2 bytes por char
                  const estimatedSizeKB = estimatedSizeBytes / 1024;
                  const maxSizeBytes = 5 * 1024 * 1024; // 5MB (limite típico sessionStorage)
                  
                  if (estimatedSizeBytes > maxSizeBytes) {
                    sendToTerminal('❌ [PROD_OPEN_FLOW] Snapshot muito grande', {
                      pipelineId,
                      estimatedSizeKB: estimatedSizeKB.toFixed(2),
                      maxSizeMB: (maxSizeBytes / 1024 / 1024).toFixed(1),
                      objectsCount: syncedObjetos.length
                    });
                    
                    logPipelineError(pipelineId, 'SNAPSHOT_TOO_LARGE', {
                      estimatedSizeKB: estimatedSizeKB.toFixed(2),
                      maxSizeMB: (maxSizeBytes / 1024 / 1024).toFixed(1),
                      objectsCount: syncedObjetos.length
                    });
                    
                    await cleanupOnCriticalError(pipelineId, 'SNAPSHOT_TOO_LARGE', {
                      openMs: metrics.openMs,
                      monitorInitMs: metrics.monitorInitMs,
                      monitorStatusMs: metrics.monitorStatusMs,
                      syncMs: metrics.syncMs
                    });
                    
                    notify({
                      type: 'error',
                      title: 'Snapshot muito grande',
                      message:
                        'O snapshot do documento excede o limite de armazenamento do navegador.\n\n' +
                        `Objetos: ${syncedObjetos.length}\n` +
                        `Tamanho: ${estimatedSizeKB.toFixed(0)} KB\n` +
                        `Limite: ${(maxSizeBytes / 1024).toFixed(0)} KB\n\n` +
                        'Ação necessária:\n' +
                        '• O arquivo será reprocessado sob demanda\n' +
                        '• Considere reduzir a complexidade do documento\n' +
                        '• Contate o suporte se o problema persistir',
                      duration: 12000,
                    });
                    return;
                  }
                  
                  sendToTerminal('💾 [PROD_OPEN_FLOW] Chamando writeSnapshot', {
                    pipelineId,
                    pid,
                    iid,
                    objetosCount: syncedObjetos.length,
                    payloadSize: estimatedSizeKB.toFixed(2) + ' KB',
                    ttl: '15s'
                  });
                  
                  writeSnapshot(pipelineId, payload, 15 * 1000);
                  
                  sendToTerminal('✅ [PROD_OPEN_FLOW] writeSnapshot concluído', {
                    pipelineId,
                    pid,
                    iid
                  });
                  
                  sendToTerminal('💾 [PROD_OPEN_FLOW] Chamando setPreparedPipelineId', {
                    pipelineId,
                    pid,
                    iid
                  });
                  
                  setPreparedPipelineId(pid, iid, pipelineId);
                  
                  sendToTerminal('✅ [PROD_OPEN_FLOW] setPreparedPipelineId concluído', {
                    pipelineId,
                    pid,
                    iid
                  });
                  
                  // ✅ AGORA marca como 'done' APÓS snapshot salvo (ordem correta!)
                  setProdPrepStep('done');
                  
                  sendToTerminal('✅ [PROD_OPEN_FLOW] Snapshot persistido com sucesso', {
                    pipelineId,
                    pid,
                    iid,
                    objetosCount: syncedObjetos.length,
                    docKey: pinnedDocKey
                  });
                  
                } catch (err) {
                  sendToTerminal('❌ [PROD_OPEN_FLOW] ERRO ao salvar snapshot', {
                    pipelineId,
                    pid,
                    iid,
                    errorName: (err as any)?.name,
                    errorMessage: err instanceof Error ? err.message : String(err),
                    errorCode: (err as any)?.code,
                    syncedObjetosLength: syncedObjetos.length,
                    stack: err instanceof Error ? err.stack?.substring(0, 200) : undefined
                  });
                  
                  // Verificar se é erro de quota
                  const isQuotaError = (err as any)?.name === 'QuotaExceededError' || 
                                     (err as any)?.code === 22 || 
                                     (err as any)?.code === 1014 ||
                                     (err as any)?.message?.toLowerCase().includes('quota');
                  
                  sendToTerminal('🔍 [PROD_OPEN_FLOW] Tipo de erro identificado', {
                    pipelineId,
                    isQuotaError,
                    errorName: (err as any)?.name
                  });
                  
                  if (isQuotaError) {
                    const estimatedSizeKB = (JSON.stringify({ 
                      meta: { total: syncedObjetos.length }, 
                      objetos: syncedObjetos 
                    }).length * 2 / 1024).toFixed(0);
                    
                    await cleanupOnCriticalError(pipelineId, 'STORAGE_QUOTA_EXCEEDED', {
                      openMs: metrics.openMs,
                      monitorInitMs: metrics.monitorInitMs,
                      monitorStatusMs: metrics.monitorStatusMs,
                      syncMs: metrics.syncMs
                    });
                    
                    notify({
                      type: 'error',
                      title: 'Armazenamento insuficiente',
                      message:
                        'O navegador não tem espaço suficiente para salvar o snapshot.\n\n' +
                        `Objetos: ${syncedObjetos.length}\n` +
                        `Tamanho estimado: ${estimatedSizeKB} KB\n\n` +
                        'Soluções:\n' +
                        '• Feche outras abas do navegador\n' +
                        '• Limpe o cache do navegador (Ctrl+Shift+Del)\n' +
                        '• Limpe dados do site nas configurações\n' +
                        '• Use modo anônimo se o problema persistir\n\n' +
                        'Contate o suporte se necessário.',
                      duration: 12000,
                    });
                  } else {
                    await cleanupOnCriticalError(pipelineId, 'SNAPSHOT_SAVE_FAILED', {
                      openMs: metrics.openMs,
                      monitorInitMs: metrics.monitorInitMs,
                      monitorStatusMs: metrics.monitorStatusMs,
                      syncMs: metrics.syncMs
                    });
                    
                    const errorMsg = err instanceof Error ? err.message : String(err);
                    notify({
                      type: 'error',
                      title: 'Erro ao salvar snapshot',
                      message: `${errorMsg}\n\nTente novamente.`,
                      duration: 10000,
                    });
                  }
                  return;
                }
                
              } catch (err) {
                sendToTerminal('❌ [PROD_OPEN_FLOW] ERRO CRÍTICO no pipeline de monitoramento', {
                  pipelineId,
                  pid,
                  iid,
                  errorName: (err as any)?.name,
                  errorMessage: err instanceof Error ? err.message : String(err),
                  errorCode: (err as any)?.code,
                  stack: err instanceof Error ? err.stack?.substring(0, 500) : undefined,
                  syncedObjetosLength: syncedObjetos?.length ?? 0,
                  monitorInitOk,
                  monitorStatusOk
                });
                
                await cleanupOnCriticalError(pipelineId, 'PIPELINE_ERROR');
                notify({
                  type: 'error',
                  title: 'Erro no monitoramento',
                  message: 'Erro no pipeline de monitoramento. Por favor, tente novamente.',
                  duration: 10000,
                });
                return;
              }
              
              // Finalização
              sendToTerminal('🔍 [PROD_OPEN_FLOW] Iniciando finalização', {
                pipelineId,
                pid,
                iid,
                syncedObjetosLength: syncedObjetos?.length ?? 0,
                hasPinnedDocKey: !!pinnedDocKey
              });
              
              try { 
                await pipelineSleep(OPERATION_DELAYS.FINALIZATION, 'finalization'); 
              } catch {}
              
              try { setProdPrepStep('done'); } catch {}
              try { setSnapshotApplying(false); } catch {}
              try { 
                setTimeout(() => setShowProdPrep(false), OPERATION_DELAYS.FINALIZATION); 
              } catch {}
              
              metrics.totalMs = Date.now() - metrics.startedAt;
              
              sendToTerminal('✅ [PROD_OPEN_FLOW] Pipeline finalizado com sucesso', {
                pipelineId,
                pid,
                iid,
                objetosCount: syncedObjetos?.length ?? 0,
                docKey: pinnedDocKey,
                totalMs: metrics.totalMs
              });
              
              logPipelineStage(pipelineId, 'FINISH', { 
                objetosCount: syncedObjetos.length,
                docKey: pinnedDocKey,
                metrics: {
                  openMs: metrics.openMs,
                  monitorInitMs: metrics.monitorInitMs,
                  monitorStatusMs: metrics.monitorStatusMs,
                  syncMs: metrics.syncMs,
                  totalMs: metrics.totalMs
                }
              });
              
              // Liberar lock de execução
              executionLockRef.current = false;
            })();
          } else {
            // ❌ Tratamento de erro quando abertura falha
            (async () => {
              await cleanupOnCriticalError(pipelineId, 'OPEN_FILE_FAILED');
              
              const errorMsg = (response as any)?.message || 'Erro desconhecido ao abrir arquivo';
              notify({
                type: 'error',
                title: 'Erro ao abrir arquivo',
                message: `${errorMsg}\n\nTente novamente.`,
                duration: 10000,
              });
            })();
          }
        });

        openRequestIdRef.current = requestId || null;

        // ✅ TIMEOUT: 20 minutos fixo para abertura de arquivo (production)
        // ✅ CORRIGIDO: Timeout não bloqueia o callback do WebView
        const TIMEOUT_ABRIR_ARQUIVO_MS = 1200000; // 20 minutos
        
        // Função recursiva para permitir múltiplos timeouts com confirmação
        const setupTimeoutProduction = (timeoutCount: number = 1) => {
          const totalMinutes = timeoutCount * 20;
          
          openTimeoutRef.current = setTimeout(() => {
            // Verificar se ainda está esperando resposta
            if (openStartedRef.current && !openCompletedRef.current) {
              logPipelineError(pipelineId, 'TIMEOUT_ABRIR_ARQUIVO', {
                totalMinutes,
                timeoutCount,
                requestId: openRequestIdRef.current
              });
              
              // Usar setTimeout para não bloquear o callback do WebView
              setTimeout(async () => {
                // Verificar novamente se ainda está esperando (callback pode ter chegado)
                if (openStartedRef.current && !openCompletedRef.current) {
                  // Obter mensagem de timeout formatada
                  const messageData = getFileOpenTimeoutMessage(totalMinutes, 20);

                  // Usar sistema de confirmação centralizado
                  const continuar = await requestConfirmation({
                    title: messageData.title,
                    message: messageData.message,
                    confirmLabel: messageData.confirmLabel,
                    cancelLabel: messageData.cancelLabel
                  });
                  
                  if (continuar) {
                    // Recursivamente criar novo timeout
                    setupTimeoutProduction(timeoutCount + 1);
                  } else {
                    await cleanupOnCriticalError(pipelineId, 'TIMEOUT_CANCELLED');
                  }
                }
              }, 0);
            }
          }, TIMEOUT_ABRIR_ARQUIVO_MS);
        };
        
        // Iniciar primeiro timeout
        setupTimeoutProduction(1);
      })();
    }

    if (!recentOpen && !caminho) {
      return;
    }

    // ✅ FASE 1.2: Cleanup de memory leaks
    return () => {
      if (openTimeoutRef.current) {
        clearTimeout(openTimeoutRef.current);
        openTimeoutRef.current = null;
      }
      if (openRequestIdRef.current) {
        openRequestIdRef.current = null;
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    role,
    selectedPedido?.orc_id,
    selectedItem?.oit_id,
  ]);
}
